/* istanbul ignore file */

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import type { AccountInfo } from "@azure/msal-browser";
import { validateEmail } from "../utils/forms";
import {
  B2C_LOCAL_STORAGE_EMAIL,
  B2C_AUTH_POLICY_VERSION,
  B2C_AUTH_POLICY_KEY,
} from "../config/constants";
import { Routes } from "../routes";
import { applicationActions, applicationThunks } from "../redux/application";
import { tokenSelector, userSelector } from "../redux/application/selectors";
import type { AppDispatch } from "../app";
import { clearFeatureFlagCache } from "./useFeatureFlag";
import type { AuthFailureReason } from "../utils/msal";

interface LoginPopupResult {
  token: string;
  user?: AccountInfo;
}
const parseSerializableUser = (userData?: AccountInfo) => ({
  ...userData,
  // @ts-expect-error - TODO - WRONG TYPE, NEED FIX
  tenantProfiles: userData?.tenantProfiles ? Object.fromEntries(userData.tenantProfiles) : {},
});

export const useAuthentication = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const token = useSelector(tokenSelector);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [username, setUsername] = useState<string>();

  const user = useSelector(userSelector);
  const email = localStorage.getItem(B2C_LOCAL_STORAGE_EMAIL);

  const PLAYWRIGHT_TOKEN = localStorage.getItem("jwtToken");

  const login = async (email: string) => {
    if (!validateEmail(email)) return;

    dispatch(applicationActions.setIsLoadingUserData(true));
    setError(false);

    try {
      const [
        { MSAL_CONFIG, getDomainHintFromEmail },
        { mslaGetUser, mslaLoginPopup },
      ] = await Promise.all([import("../utils/auth"), import("../utils/msal")]);
      const emailDomain = getDomainHintFromEmail(email);
      const cfg = MSAL_CONFIG(email, emailDomain);

      const loginPopup = () =>
        new Promise<LoginPopupResult>((resolve, reject) => {
          mslaLoginPopup(
            cfg,
            async (token: string) => {
              const userData = await mslaGetUser(cfg);
              resolve({ token, user: userData });
            },
            reject,
          );
        });

      localStorage.setItem(B2C_LOCAL_STORAGE_EMAIL, email);
      const { token, user } = await loginPopup();

      dispatch(applicationActions.setToken(token));
      dispatch(applicationActions.setUserData(parseSerializableUser(user)));
      setIsAuthenticated(true);
    } catch (err) {
      console.error("Error on login:", err);
      localStorage.removeItem(B2C_LOCAL_STORAGE_EMAIL);
      dispatch(applicationActions.clearUserData());
      setError(true);
    } finally {
      dispatch(applicationActions.setIsLoadingUserData(false));
    }
  };

  const logout = async () => {
    const [{ mslaLogout }, { MSAL_CONFIG }] = await Promise.all([
      import("../utils/msal"),
      import("../utils/auth"),
    ]);
    await mslaLogout(MSAL_CONFIG());
    localStorage.removeItem(B2C_LOCAL_STORAGE_EMAIL);
    localStorage.removeItem(B2C_AUTH_POLICY_KEY);
    dispatch(applicationActions.clearUserData());
    delete axios.defaults.headers.common.Authorization;
    clearFeatureFlagCache();
    navigate(Routes.LoginPage);
    const { default: posthog } = await import("posthog-js");
    posthog?.reset();
  };

  // Check for policy changes and clear cache if needed
  useEffect(() => {
    const currentPolicyVersion = localStorage.getItem(B2C_AUTH_POLICY_KEY);

    if (
      currentPolicyVersion &&
      currentPolicyVersion !== B2C_AUTH_POLICY_VERSION &&
      !PLAYWRIGHT_TOKEN
    ) {
      // Policy changed - clear everything to avoid invalid token issues
      localStorage.clear();
      clearFeatureFlagCache();
      localStorage.setItem(B2C_AUTH_POLICY_KEY, B2C_AUTH_POLICY_VERSION);
      // Force reload to reset state completely
      window.location.reload();
      return;
    }

    // Set current policy version if not set
    if (!currentPolicyVersion) {
      localStorage.setItem(B2C_AUTH_POLICY_KEY, B2C_AUTH_POLICY_VERSION);
    }
  }, []);

  useEffect(() => {
    if (PLAYWRIGHT_TOKEN) {
      dispatch(applicationActions.setToken(PLAYWRIGHT_TOKEN));
      setIsAuthenticated(true);
      setIsLoading(false);
      import("jwt-decode").then(({ jwtDecode }) => {
        dispatch(applicationActions.setUserData(jwtDecode(PLAYWRIGHT_TOKEN)));
      });
    } else {
      const onLoginPage = pathname === `${Routes.LoginPage}`;
      const shouldGetToken = Boolean(email) && (onLoginPage || !token);

      if (!shouldGetToken || !email) {
        setIsLoading(false);
        return;
      }

      void Promise.all([import("../utils/auth"), import("../utils/msal")])
        .then(
          async ([
            { MSAL_CONFIG, getDomainHintFromEmail },
            { mslaGetToken, mslaGetUser },
          ]) => {
            const msalConfig = MSAL_CONFIG(
              email,
              getDomainHintFromEmail(email),
            );
            const msalUser = mslaGetUser(msalConfig);

            mslaGetToken(
              msalConfig,
              async (newToken: string) => {
                dispatch(applicationActions.setToken(newToken));
                const userData = await msalUser;
                dispatch(
                  applicationActions.setUserData(
                    parseSerializableUser(userData),
                  ),
                );
              },
              (reason: AuthFailureReason) => {
                // Only a confirmed "expired" session (MSAL asked for
                // interaction) drops the user. Transient failures (network,
                // server errors) keep the stored session so the next attempt
                // can refresh silently instead of forcing a full login.
                if (reason === "expired") {
                  dispatch(applicationActions.clearUserData());
                  setError(true);
                }
                setIsLoading(false);
              },
            );
          },
        )
        .catch((authError) => {
          console.error("Error preparing auth session:", authError);
          dispatch(applicationActions.clearUserData());
          setError(true);
          setIsLoading(false);
        });
    }
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      setIsAuthenticated(true);
      if (!user?.user_id) dispatch(applicationThunks.fetchUserData());
      setIsLoading(false);
    }
  }, [token, dispatch, user?.user_id]);

  useEffect(() => {
    const givenName = user?.given_name;
    const familyName = user?.family_name;

    setUsername(
      (givenName && familyName && `${givenName} ${familyName}`) ||
        user?.name ||
        user?.email ||
        "",
    );
  }, [user]);

  return {
    error,
    login,
    logout,
    isAuthenticated,
    isLoading,
    username,
  };
};
